import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import StatusSummary from "../components/dashboard/status-summary";
import PerformanceCharts from "../components/dashboard/performance-charts";
import AlertsList from "../components/dashboard/alerts-list";
import DeviceTable from "../components/dashboard/device-table";
import { queryClient } from "../lib/queryClient";
import { RefreshCw } from "lucide-react";
import { useAppDispatch } from "../store/hooks";
import { setDevices } from "../store/slices/deviceSlice";
import { useQuery } from "@tanstack/react-query";
import { getDevices } from "../api/axios";

export default function Dashboard() {
  const [timeRange, setTimeRange] = useState("last24hours");
  const dispatch = useAppDispatch();
  // const devices = useAppSelector((state) => state.devices.devices); // Uncomment if needed

  // Fetch devices and sync with Redux
  const { data: fetchedDevices } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (fetchedDevices) {
      dispatch(setDevices(fetchedDevices));
    }
  }, [fetchedDevices, dispatch]);

  useEffect(() => {
    // Set up WebSocket event listener for real-time updates
    const handleWsMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
      
      if (data.type === "device_status_update") {
        queryClient.invalidateQueries({ queryKey: ["/api/device-status-summary"] });
        queryClient.invalidateQueries({ queryKey: ["/api/devices"] });
      } else if (data.type === "new_alert") {
        queryClient.invalidateQueries({ queryKey: ["/api/alerts/recent"] });
      } else if (data.type === "performance_update") {
        queryClient.invalidateQueries({ queryKey: ["/api/performance-metrics"] });
      }
    };

    window.addEventListener("ws-message", handleWsMessage);

    // Subscribe to real-time updates
    return () => {
      window.removeEventListener("ws-message", handleWsMessage);
    };
  }, []);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/device-status-summary"] });
    queryClient.invalidateQueries({ queryKey: ["/api/devices"] });
    queryClient.invalidateQueries({ queryKey: ["/api/alerts/recent"] });
    queryClient.invalidateQueries({ queryKey: ["/api/performance-metrics"] });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark">
              Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Overview of your network infrastructure health and performance
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-2 sm:space-y-0">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last24hours">Last 24 hours</SelectItem>
                <SelectItem value="last7days">Last 7 days</SelectItem>
                <SelectItem value="last30days">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Status Summary Cards */}
      <StatusSummary />

      {/* Recent Alerts Section */}
      <AlertsList />
      
      {/* Performance Charts Section */}
      <PerformanceCharts timeRange={timeRange}   />

      {/* Device Status Table Section */}
      <DeviceTable />
    </div>
  );
}
